import {
  Box,
  Button,
  colors,
  Skeleton,
  Typography,
  useTheme,
} from '@mui/material';
import TheatersIcon from '@mui/icons-material/Theaters';
import AudiotrackIcon from '@mui/icons-material/Audiotrack';
import FormatShapesIcon from '@mui/icons-material/FormatShapes';
import NotesIcon from '@mui/icons-material/Notes';
import AppsIcon from '@mui/icons-material/Apps';
import ImageIcon from '@mui/icons-material/Image';
import DescriptionIcon from '@mui/icons-material/Description';
import TableChartIcon from '@mui/icons-material/TableChart';
import SlideshowIcon from '@mui/icons-material/Slideshow';
import DataObjectIcon from '@mui/icons-material/DataObject';
import TextSnippetIcon from '@mui/icons-material/TextSnippet';
import DownloadIcon from '@mui/icons-material/Download';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ExoFilePreview } from '../ExoFilePreview';
import { tokens } from '../../../global/theme/tokens';

export const MediaVisualisation = {
  image: ImageIcon,
  application: AppsIcon,
  text: NotesIcon,
  audio: AudiotrackIcon,
  video: TheatersIcon,
  font: FormatShapesIcon,
};

const MediaPreview = ({
  size,
  src,
  mimeType = 'image',
  onlyIcons,
  type,
  allowPreview,
  allowDownload,
  onPreviewClick,
}) => {
  const { t } = useTranslation();
  const PreviewElement = MediaVisualisation[mimeType.split('/')[0]];
  // check validation of the url

  const [imgSrc, setImgSrc] = useState('');
  const [allowPreviewState, setAllowPreviewState] = useState(allowPreview);
  const [imageSize, setImageSize] = useState(size || '40px');
  const [isIcon, setIsIcon] = useState(false);
  const [docType, setDocType] = useState(null);

  useEffect(() => {
    checkImageValidity(src, mimeType);
  }, [src]);

  const [isValidUrl, setIsValidUrl] = useState(false);
  const [isChecking, setIsChecking] = useState(true);

  const checkImageValidity = (src, mimeType) => {
    const imgSize = '60%';
    const img = new Image();

    img.onload = () => {
      setIsValidUrl(true);
      setIsChecking(false);
    };

    img.onerror = () => {
      setIsValidUrl(false);
      setIsChecking(false);
    };

    const mimeTypeArray = mimeType.split('/');
    const mimeTypeFirst = mimeTypeArray[0];
    const mimeTypeLast = mimeTypeArray[mimeTypeArray.length - 1];

    const extension = getFileExtension(src);

    // Reset allowPreviewState based on prop for each check
    setAllowPreviewState(allowPreview);
    setDocType(null);

    // Check non-image types by extension FIRST, before mimeType fallback.
    // This prevents large files (videos, audio, etc.) from being downloaded
    // when mimeType defaults to 'image'.
    if (
      mimeTypeFirst === 'video' ||
      extension == 'mov' ||
      extension == 'mp4' ||
      extension == 'wmv' ||
      extension == 'avi' ||
      extension == 'avchd' ||
      extension == 'flv' ||
      extension == 'mkv' ||
      extension == 'webm'
    ) {
      img.src = '/assets/icons/play.webp';
      setImageSize(imgSize);
      setIsIcon(true);
    } else if (
      mimeTypeFirst === 'audio' ||
      extension == 'aac' ||
      extension == 'aiff' ||
      extension == 'cda' ||
      extension == 'flac' ||
      extension == 'mp3' ||
      extension == 'ogg' ||
      extension == 'wav' ||
      extension == 'wma'
    ) {
      img.src = '/assets/icons/music.webp';
      setAllowPreviewState(false);
      setImageSize(imgSize);
      setIsIcon(true);
    } else if (extension == 'zip' || extension == 'rar' || extension == '7z') {
      img.src = '/assets/icons/safe.webp';
      setAllowPreviewState(false);
      setImageSize(imgSize);
      setIsIcon(true);
    } else if (
      extension === 'txt' ||
      extension === 'md' ||
      extension === 'log' ||
      mimeType === 'text/plain' ||
      mimeType === 'text/markdown'
    ) {
      setDocType('text');
      setImageSize(imgSize);
      setIsIcon(true);
      setIsValidUrl(true);
      setIsChecking(false);
      return;
    } else if (
      extension === 'doc' ||
      extension === 'docx' ||
      extension === 'rtf' ||
      extension === 'odt' ||
      mimeTypeLast === 'msword' ||
      mimeTypeLast === 'vnd.openxmlformats-officedocument.wordprocessingml.document'
    ) {
      setDocType('word');
      setImageSize(imgSize);
      setIsIcon(true);
      setIsValidUrl(true);
      setIsChecking(false);
      return;
    } else if (
      extension === 'xls' ||
      extension === 'xlsx' ||
      extension === 'csv' ||
      extension === 'ods' ||
      mimeTypeLast === 'vnd.ms-excel' ||
      mimeTypeLast === 'vnd.openxmlformats-officedocument.spreadsheetml.sheet'
    ) {
      setDocType('excel');
      setImageSize(imgSize);
      setIsIcon(true);
      setIsValidUrl(true);
      setIsChecking(false);
      return;
    } else if (
      extension === 'ppt' ||
      extension === 'pptx' ||
      extension === 'odp' ||
      mimeTypeLast === 'vnd.ms-powerpoint' ||
      mimeTypeLast === 'vnd.openxmlformats-officedocument.presentationml.presentation'
    ) {
      setDocType('powerpoint');
      setImageSize(imgSize);
      setIsIcon(true);
      setIsValidUrl(true);
      setIsChecking(false);
      return;
    } else if (
      mimeTypeFirst !== 'image' &&
      extension !== 'svg' &&
      (extension === 'xml' || mimeTypeLast === 'xml' || mimeTypeLast.endsWith('+xml'))
    ) {
      setDocType('xml');
      setImageSize(imgSize);
      setIsIcon(true);
      setIsValidUrl(true);
      setIsChecking(false);
      return;
    } else if (
      mimeTypeFirst === 'image' ||
      extension == 'jpg' ||
      extension == 'png' ||
      extension == 'svg' ||
      extension == 'tiff' ||
      extension == 'eps' ||
      extension == 'gif' ||
      extension == 'bmp'
    ) {
      setIsIcon(false);
      img.src = src;
    } else if (
      (mimeTypeFirst === 'application' && mimeTypeLast === 'pdf') ||
      extension == 'pdf'
    ) {
      setIsIcon(false);
      img.src = src;
    } else {
      img.src = '/assets/icons/binary.webp';
      setAllowPreviewState(false);
      setImageSize(imgSize);
      setIsIcon(true);
    }

    setImgSrc(img.src);
  };

  function getFileExtension(url) {
    // Remove query parameters (anything after '?')
    if (!url) return '';
    const cleanUrl = url.split('?')[0];

    // Extract the file extension
    const extension = cleanUrl.split('.').pop();

    // Ensure we actually got a file extension (and not the whole URL)
    return extension.includes('/') ? '' : extension;
  }

  // preview - a custom onPreviewClick replaces the built-in ExoFilePreview
  const [openPreview, setOpenPreview] = useState(null);
  function handleOpen() {
    if (onPreviewClick) {
      onPreviewClick();
      return;
    }
    setOpenPreview(true);
  }

  function handlePreviewClose() {
    setOpenPreview(false);
  }

  return (
    <>
      {isChecking ? (
        <Skeleton
          sx={{
            transform: 'none',
            minWidth: size,
            height: imageSize,
            aspectRatio: '1',
          }}
        />
      ) : (
        <>
          {!onlyIcons ? (
            <>
              {isValidUrl ? (
                <RenderPreview
                  isIcon={isIcon}
                  previewSrc={imgSrc}
                  src={src}
                  size={size}
                  imageSize={imageSize}
                  type={type}
                  onClick={allowPreviewState && handleOpen}
                  docType={docType}
                  allowDownload={allowDownload}
                />
              ) : (
                <Box
                  sx={{ minWidth: size, height: imageSize }}
                  className="relative"
                >
                  <Typography
                    textAlign={'center'}
                    fontSize={8}
                    fontWeight={500}
                    className="uppercase absolute w-full h-full flex justify-center items-center opacity-50"
                  >
                    {mimeType.split('/')[1] || t('No Preview')}
                  </Typography>
                </Box>
              )}
            </>
          ) : (
            <PreviewElement style={{ fontSize: imageSize }} />
          )}
        </>
      )}
      {allowPreviewState && !onPreviewClick && (
        <ExoFilePreview
          open={openPreview}
          previewSrc={imgSrc}
          src={src}
          mimeType={mimeType}
          onClose={handlePreviewClose}
        />
      )}
    </>
  );
};

const docTypeIconMap = {
  word: { Icon: DescriptionIcon, color: '#2B579A' },
  excel: { Icon: TableChartIcon, color: '#217346' },
  powerpoint: { Icon: SlideshowIcon, color: '#D24726' },
  xml: { Icon: DataObjectIcon, color: '#E67E22' },
  text: { Icon: TextSnippetIcon, color: '#7F8C8D' },
};

// Wrapper for the doc-type icon tiles: no inline baseline gap, no extra height
const docTileWrapperSx = {
  position: 'relative',
  display: 'inline-flex',
  verticalAlign: 'top',
  lineHeight: 0,
  flexShrink: 0,
};

const RenderPreview = ({
  previewSrc,
  src,
  size,
  imageSize,
  type,
  onClick,
  isIcon,
  docType,
  allowDownload,
}) => {
  const theme = useTheme();
  const mode = theme.palette.mode;
  const colors = tokens(theme.palette.mode, theme.palette.colorTheme);

  const filterMap = {
    dark: 'brightness(0) saturate(100%) invert(99%) sepia(66%) saturate(10%) hue-rotate(3deg) brightness(116%) contrast(84%)',
    light:
      'brightness(0) saturate(100%) invert(26%) sepia(1%) saturate(4686%) hue-rotate(2deg) brightness(117%) contrast(64%)',
  };

  const handleDownload = (e) => {
    e.stopPropagation();
    window.open(src, '_blank');
  };

  const DownloadButton = () => (
    <Button
      onClick={handleDownload}
      sx={{
        position: 'absolute',
        bottom: 4,
        right: 4,
        minWidth: 'unset',
        padding: '4px',
        background: colors.bgInput,
        borderRadius: '50%',
        '&:hover': {
          background: colors.selected,
        },
      }}
    >
      <DownloadIcon sx={{ fontSize: '16px' }} />
    </Button>
  );

  // Render MUI Icon for Office / XML documents
  if (docType && docTypeIconMap[docType]) {
    const { Icon, color } = docTypeIconMap[docType];
    // The icon scales with the tile itself (works for px values as well as
    // percentages like the grid view's size="100%"): the tile is a square via
    // aspect-ratio and the icon takes 70% of its width/height.
    const boxSize = size || '40px';
    const iconSx = { width: '70%', height: '70%', color };
    // Fluid tiles (e.g. grid view with size="100%") fill their parent, so give
    // them an inner margin; fixed px tiles are already sized by the caller.
    const isFluid = typeof boxSize === 'string' && boxSize.trim().endsWith('%');
    const wrapperSx = isFluid
      ? { ...docTileWrapperSx, width: boxSize, aspectRatio: '1 / 1', padding: '10%', boxSizing: 'border-box' }
      : docTileWrapperSx;
    const tileSize = isFluid ? '100%' : boxSize;
    if (onClick) {
      return (
        <Box sx={wrapperSx}>
          <Button
            onClick={onClick}
            sx={{
              minWidth: 'unset',
              minHeight: 'unset',
              padding: 0,
              width: tileSize,
              height: tileSize,
              aspectRatio: '1 / 1',
              lineHeight: 0,
              display: 'flex',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              background: colors.bgInput,
              borderRadius: '9.5px',
            }}
          >
            <Icon sx={iconSx} />
          </Button>
          {allowDownload && <DownloadButton />}
        </Box>
      );
    }
    return (
      <Box sx={wrapperSx}>
        <Box
          sx={{
            width: tileSize,
            height: tileSize,
            aspectRatio: '1 / 1',
            lineHeight: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            background: colors.bgInput,
            borderRadius: '9.5px',
          }}
        >
          <Icon sx={iconSx} />
        </Box>
        {allowDownload && <DownloadButton />}
      </Box>
    );
  }

  if (onClick)
    return (
      <Box sx={{ position: 'relative' }}>
        <Button
          onClick={onClick}
          sx={{
            minWidth: 'unset',
            padding: 0,
            background: isIcon ? colors.bgInput : 'transparent',
          }}
        >
          <img
            src={previewSrc}
            className="w-full aspect-square object-contain object-center"
            style={{
              borderRadius: '9.5px',
              width: size,
              height: imageSize,
              filter: 'unset',
              opacity: isIcon ? '0.2' : '1',
            }}
          />
        </Button>
        {allowDownload && <DownloadButton />}
      </Box>
    );

  return (
    <Box sx={{ position: 'relative', display: 'inline-block' }}>
      <img
        src={previewSrc}
        className="w-full aspect-square object-contain object-center"
        style={{
          borderRadius: '9.5px',
          width: size,
          height: imageSize,
          filter: 'unset',
          opacity: isIcon ? '0.2' : '1',
        }}
      />
      {allowDownload && <DownloadButton />}
    </Box>
  );
};

export default MediaPreview;
